import * as React from 'react';
import {
  CheckboxType,
  Form,
  FormCheckboxGroup,
  FormSelect,
  FormTextField,
  Grid,
  IOption,
  requiredRule,
  UseFormReturn,
} from '@gcl/react-core';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import { ICompanyType } from '@company/companyType/companyType.models';
import { IOrganizationTypeOption } from '@company/organizationType/organizationType.models';
import { IProjectCompany } from '@company/projectCompany/projectCompany.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { NULL_OPEN_PORTAL_EVENT, SITE_DESIGN_TEAM, SITE_OPERATIONS_TEAM_NAME } from '@common/constants';
import '@gcl/react-core/dist/themes.scss';
import { useFetchAllPermittedRiskCategories } from '@riskCategory/riskCategory.queries';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import { ECompanyTypeNameConst } from '@company/companyType/companyType.constants';
import usePrompt from '@common/prompt.hooks';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { UseQueryResult } from '@tanstack/react-query';
import { ECompanyFormKeys, ECompanyFormType, ICompanyFormState } from './companyModal.models';

export interface ICompanyFormProps {
  company?: IProjectCompany;
  useFormMethods: UseFormReturn;
  errorUpdateProjectCompany?: AxiosErrorResponse | null;
  errorCreateProjectCompany?: AxiosErrorResponse | null;
  companyTypeOptions?: IOption<ICompanyType>[];
  organizationTypeOptions?: IOption<IOrganizationTypeOption>[];
  type?: ECompanyFormType;
  isProjectRequestDefaultCompany?: boolean;
  setIsFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CompanyForm: React.FunctionComponent<ICompanyFormProps> = ({
  company,
  useFormMethods,
  errorUpdateProjectCompany,
  errorCreateProjectCompany,
  companyTypeOptions = [],
  organizationTypeOptions = [],
  type = ECompanyFormType.USERS,
  isProjectRequestDefaultCompany = false,
  setIsFormDirty,
}) => {
  const isGemsMaster = true;
  const selectedCompanyTypeOption = useFormMethods.watch(ECompanyFormKeys.TYPE);
  const localCompanyTypeOptions = React.useMemo(() => {
    if (!!company && isPositiveNumber((company as IProjectCompany).id)) return companyTypeOptions;
    return companyTypeOptions.filter((o) => !!o.data?.allowCreation);
  }, [JSON.stringify(companyTypeOptions), JSON.stringify(company)]);

  const {
    formState: { isDirty },
  } = useFormMethods;

  React.useEffect(() => {
    setIsFormDirty(isDirty);
    return () => {
      setIsFormDirty(false);
    };
  }, [isDirty]);

  const { data: riskCategoryOptions } = (
    useFetchAllPermittedRiskCategories({ isDirty }) as () => UseQueryResult<
      IOption<IRiskCategory>[],
      AxiosErrorResponse
    >
  )();

  const localSetValue = (fieldName: string, value: any) => {
    useFormMethods.setValue(fieldName, value, {
      shouldDirty: false,
    });
  };

  const { renderModal, openPortal } = useConfirmationModal({
    content: `All selected users will be removed. Please confirm that you would like to change the company type?`,
    onConfirm: () => {
      // TODO clear all company's member
    },
    onCancel: () => {
      if (!!company && company.companyTypeId)
        localSetValue(
          ECompanyFormKeys.TYPE,
          localCompanyTypeOptions.find((ct) => ct.data?.id === company.companyTypeId),
        );
    },
  });

  React.useEffect(() => {
    if (!company || !selectedCompanyTypeOption || type === ECompanyFormType.USERS) return;

    if (company.companyTypeId !== selectedCompanyTypeOption.data.id) {
      openPortal(NULL_OPEN_PORTAL_EVENT);
    }
  }, [JSON.stringify(selectedCompanyTypeOption), JSON.stringify(company)]);

  const getDefaultValues = (
    companyLocal?: IProjectCompany,
    riskCategoryOptionsLocal?: IOption<IRiskCategory>[],
    localCompanyTypeOptionsLocal?: IOption<ICompanyType>[],
    organizationTypeOptionsLocal?: IOption<IOrganizationTypeOption>[],
  ) => {
    // riskCat
    let tempRiskCatStr: string = ERiskCategory.A;

    const companyRiskCat: string | IRiskCategory | undefined = companyLocal
      ? companyLocal?.maxRiskCategory ?? companyLocal[ECompanyFormKeys.MAX_RISK_CATEGORY as keyof IProjectCompany]
      : undefined;

    if (!companyRiskCat || typeof companyRiskCat === 'string') {
      tempRiskCatStr = companyRiskCat ?? ERiskCategory.A;
    }
    if (typeof companyRiskCat === 'object') {
      tempRiskCatStr = (companyRiskCat as IRiskCategory).category;
    }

    const tempRiskCat = riskCategoryOptionsLocal?.find((rco) => {
      return rco.data?.category.toString() === tempRiskCatStr;
    });

    // companyType
    let companyType: IOption<ICompanyType> | null = null;
    if (!companyLocal) {
      companyType =
        localCompanyTypeOptionsLocal && localCompanyTypeOptionsLocal.length > 0
          ? localCompanyTypeOptionsLocal[0]
          : null;
    } else {
      companyType = localCompanyTypeOptionsLocal?.find((ct) => ct.data?.id === companyLocal?.companyTypeId) ?? null;
    }

    // organizationType
    let organizationType = organizationTypeOptionsLocal?.find(
      (oto) =>
        oto.key ===
        (company
          ? company?.organizationTypeId ?? company[ECompanyFormKeys.ORGANIZATION_TYPE_ID as keyof typeof company]
          : ''),
    );
    if (!organizationType && !company)
      organizationType = organizationTypeOptionsLocal?.find((oto) => oto.data?.organizationTypeCode === 'EXT');

    const state: ICompanyFormState = {
      [ECompanyFormKeys.NAME]: companyLocal?.name || null,
      [ECompanyFormKeys.CODE]: companyLocal?.code || null,
      [ECompanyFormKeys.MAX_RISK_CATEGORY]: tempRiskCat ?? null,
      [ECompanyFormKeys.TYPE]: companyType,
      [ECompanyFormKeys.ORGANIZATION_TYPE]: organizationType ? [organizationType] : [],
    };
    return state;
  };

  return (
    <>
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorCreateProjectCompany ?? null, genericError: 'Create company failed' },
          { error: errorUpdateProjectCompany ?? null, genericError: 'Update company failed' },
        ]}
      />
      <Form
        defaultValues={{
          callback: () => {
            const state = getDefaultValues(
              company,
              riskCategoryOptions,
              localCompanyTypeOptions,
              organizationTypeOptions,
            );
            return state;
          },
          deps: [company, riskCategoryOptions, localCompanyTypeOptions, organizationTypeOptions],
        }}
        enableUnsavedChangesPrompt
        unsavedChangesPromptProps={{
          usePrompt,
        }}
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ECompanyFormKeys.TYPE} text="Type" required />
                <FormSelect
                  options={localCompanyTypeOptions}
                  formFieldMethods={formFieldMethods}
                  id={ECompanyFormKeys.TYPE}
                  rules={requiredRule}
                  allowSearch={false}
                  clearable={false}
                  multiple={false}
                  disabled={type === ECompanyFormType.USERS && !!company}
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ECompanyFormKeys.NAME} text="Name" required />
                <FormTextField
                  formFieldMethods={formFieldMethods}
                  id={ECompanyFormKeys.NAME}
                  rules={requiredRule}
                  disabled={
                    !!company &&
                    (company.name.trim() === SITE_OPERATIONS_TEAM_NAME || company.name.trim() === SITE_DESIGN_TEAM)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage labelFor={ECompanyFormKeys.CODE} text="Code" required />
                <FormTextField formFieldMethods={formFieldMethods} id={ECompanyFormKeys.CODE} rules={requiredRule} />
              </Grid>
              <Grid
                item
                xs={12}
                className={
                  selectedCompanyTypeOption?.data?.name.trim() === ECompanyTypeNameConst.SYSTEM_USERS ||
                  selectedCompanyTypeOption?.data?.name.trim() === ECompanyTypeNameConst.VIEWERS
                    ? 'gcl-hide'
                    : ''
                }
              >
                <LabelWithMessage labelFor={ECompanyFormKeys.ORGANIZATION_TYPE} text="Organization Type" required />
                <FormCheckboxGroup
                  checkOptions={organizationTypeOptions}
                  type={CheckboxType.Radio}
                  formFieldMethods={formFieldMethods}
                  id={ECompanyFormKeys.ORGANIZATION_TYPE}
                  rules={requiredRule}
                  disabled={
                    (type === ECompanyFormType.USERS && !isGemsMaster) ||
                    (type === ECompanyFormType.PROJECT_REQUEST && isProjectRequestDefaultCompany)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <LabelWithMessage
                  labelFor={ECompanyFormKeys.MAX_RISK_CATEGORY}
                  text="Permitted Temp Work Risk Category"
                  required
                />
                <FormSelect
                  options={riskCategoryOptions || []}
                  formFieldMethods={formFieldMethods}
                  id={ECompanyFormKeys.MAX_RISK_CATEGORY}
                  rules={requiredRule}
                  allowSearch={false}
                  clearable={false}
                  multiple={false}
                  disabled={
                    (type === ECompanyFormType.USERS && !!company) ||
                    (type === ECompanyFormType.PROJECT_REQUEST && isProjectRequestDefaultCompany)
                  }
                />
              </Grid>
            </Grid>
          );
        }}
      />
      {renderModal(false, false, false)}
    </>
  );
};
